import React, { Fragment, useState } from "react";

import { Box, makeStyles } from "@material-ui/core";

import PendingLearners from "@ds-developer/pending-learners";

import { RejectLearnerFormFields } from "@ds-developer/reject-learner-modal";
import Content from "@ds-developer/content";
import PageHeader from "@ds-developer/page-header";

import { AddNewLearnerButton, LearnersTable } from "./components";
import { ManageLearnersProps } from "./types";
import { learnerLessonTypeToString } from "./utils";
import { useAuth } from "context/AuthProvider";
import { useTealium } from "context/TealiumProvider";
import FeatureNotAvailable from "@hubcapp/views/FeatureNotAvailable";
import useMutationRejectLearner from "./hooks/useMutationRejectLearner";

const useStyles = makeStyles(theme => ({
  content: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  pendingLearnersWrapper: {
    [theme.breakpoints.down("sm")]: {
      marginTop: "24px",
    },
  },
}));

const ManageLearners: React.FC<ManageLearnersProps> = ({
  pendingLearners,
  learners,
  loading,
  ableToAddLearners,
}) => {
  const { instructor } = useAuth();
  if (instructor.status !== "Active") {
    return <FeatureNotAvailable />;
  }

  const classes = useStyles();
  const { trackEvent } = useTealium();

  const [submittingRejectLearner, setSubmittingRejectLearner] = useState(false);
  const [rejectLearnerModalOpen, setRejectLearnerModalOpen] = useState(false);
  const [rejectedLearnerId, setRejectedLearnerId] = useState<string>("");

  const [
    rejectLearner,
    { loading: loadingRejectLearner },
  ] = useMutationRejectLearner(instructor.id, rejectedLearnerId);

  const handleSetRejectLearnerModalOpen = (value: boolean, id?: string) => {
    setRejectedLearnerId(id ?? "");
    setRejectLearnerModalOpen(value);
  };

  const submit = (values: RejectLearnerFormFields) => {
    setSubmittingRejectLearner(true);
    if (trackEvent) {
      trackEvent({
        eventCategory: "Learner",
        eventAction: "Button Click",
        eventLabel: "Confirm Rejection",
      });
    }
    rejectLearner({
      variables: {
        learnerId: rejectedLearnerId,
        instructorId: instructor.id,
        rejectionReason: values.reason,
      },
    })
      .then(() => {
        setSubmittingRejectLearner(false);
        setRejectLearnerModalOpen(false);
        setRejectedLearnerId("");
      })
      .catch(() => {
        setSubmittingRejectLearner(false);
      });
  };

  return (
    <Content>
      <PageHeader
        breadcrumbs={[{ link: "/", title: "Home" }, { title: "Learners" }]}
        title="Manage learners"
        actionButton={
          ableToAddLearners ? <AddNewLearnerButton /> : <Fragment />
        }
      />
      <div className={classes.content}>
        <Box mb={3} className={classes.pendingLearnersWrapper}>
          <PendingLearners
            learners={
              pendingLearners?.map(
                ({
                  id,
                  created,
                  name: { fullName, lastName, firstName },
                  pickupAddress,
                  isShortNoticeTest,
                  learnerLessonType,
                  lessons,
                }) => ({
                  id,
                  created,
                  isShortNoticeTest: isShortNoticeTest ?? false,
                  learnerLessonType: learnerLessonTypeToString(
                    learnerLessonType,
                  ),
                  firstName,
                  lastName,
                  fullName,
                  postcode: pickupAddress?.postcode ?? "",
                  address: pickupAddress?.line1 ?? "",
                  firstLesson:
                    lessons?.sort(
                      (a, b) =>
                        new Date(a.startDateTime).getTime() -
                        new Date(b.startDateTime).getTime(),
                    )?.[0] ?? undefined,
                }),
              ) ?? []
            }
            submit={submit}
            submitting={submittingRejectLearner}
            loadingRejectLearner={loadingRejectLearner}
            rejectLearnerModalOpen={rejectLearnerModalOpen}
            setRejectLearnerModalOpen={handleSetRejectLearnerModalOpen}
          />
        </Box>
        <LearnersTable learners={learners} loading={loading} />
      </div>
    </Content>
  );
};

export default ManageLearners;
